import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';

import { CONTAINER, EYEBROW, NAMESPACE, Picture, Rich, SECTION, SECTION_HEAD_CENTER, SECTION_LEAD, SECTION_TITLE } from '../components/ui';
import { numberFormatter } from '../strings';

const STATS: { key: string; count: number; bar: string; word?: boolean }[] = [
  { key: 'players', count: 3000000, bar: '100%', word: true },
  { key: 'servers', count: 2000, bar: '82%' },
  { key: 'countries', count: 150, bar: '68%' },
  { key: 'games', count: 3000, bar: '90%' },
];
const QUOTES = [
  { key: 'sacy', name: 'Sacy', photo: 'pro-sacy' },
  { key: 'coldzera', name: 'coldzera', photo: 'pro-coldzera' },
  { key: 'brtt', name: 'brTT', photo: 'pro-brtt' },
];

const STAT_CARD = 'rounded-[1rem] border border-white/10 bg-[linear-gradient(180deg,rgb(255_255_255/.06),rgb(255_255_255/.015))] p-[clamp(1rem,.8rem+1vw,1.5rem)] shadow-[0_2px_6px_rgb(0_0_0/.45),0_1px_2px_rgb(0_0_0/.3),inset_0_1px_0_rgb(255_255_255/.05)] backdrop-blur-[8px] forced-colors:border forced-colors:border-[CanvasText] forced-colors:bg-[Canvas]';
const STAT_BAR = 'mt-[.85rem] block h-1 overflow-hidden rounded-[2px] bg-white/[.08] forced-colors:border forced-colors:border-[CanvasText] forced-colors:bg-[Canvas] forced-colors:[forced-color-adjust:none] [&>i]:block [&>i]:h-full [&>i]:w-[calc(var(--w)*var(--p,1))] [&>i]:rounded-[2px] [&>i]:bg-[linear-gradient(90deg,rgb(147_255_24/.45),var(--accent))] [&>i]:shadow-[0_0_12px_rgb(147_255_24/.45)] forced-colors:[&>i]:bg-[CanvasText] forced-colors:[&>i]:bg-none forced-colors:[&>i]:shadow-none';
const QUOTE_CARD = 'grid content-between gap-5 rounded-[1rem] border border-white/10 bg-[linear-gradient(180deg,rgb(255_255_255/.055),rgb(255_255_255/.012))] p-[clamp(1.1rem,.85rem+1vw,1.6rem)] shadow-[0_8px_24px_rgb(0_0_0/.45),0_2px_6px_rgb(0_0_0/.3)] forced-colors:border forced-colors:border-[CanvasText] forced-colors:bg-[Canvas] [@media(width>=40rem)_and_(width<56rem)]:grid-cols-[minmax(0,1fr)_13rem] [@media(width>=40rem)_and_(width<56rem)]:items-center [@media(width>=40rem)_and_(width<56rem)]:gap-6';

export function Scale() {
  const { t } = useTranslation(NAMESPACE);
  const { locale = 'pt' } = useRouter();
  const format = numberFormatter(locale);

  return (
    <section className={SECTION} id='escala' aria-labelledby='scale-title'>
      <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__map' />
        <span className='sec__halo sec__halo--w' />
      </div>
      <div className={CONTAINER}>
        <div className={SECTION_HEAD_CENTER} data-reveal>
          <p className={EYEBROW}>{t('scale.eyebrow')}</p>
          <h2 className={SECTION_TITLE} id='scale-title'>
            <Rich i18nKey='scale.title' />
          </h2>
          <p className={`${SECTION_LEAD} mx-auto`}>{t('scale.lead')}</p>
        </div>

        <ul className='m-0 grid list-none grid-cols-[1fr] gap-[clamp(.75rem,.5rem+1vw,1.25rem)] p-0 [@media(width>=32.5rem)]:grid-cols-2 [@media(width>=64rem)]:grid-cols-4' data-reveal-group data-counters>
          {STATS.map((stat) => {
            const final = stat.word ? t(`scale.stats.${stat.key}.value`) : format(stat.count);
            return (
              <li key={stat.key} className={STAT_CARD}>
                <p className='tabular whitespace-normal text-[clamp(1.6rem,.55rem+2.3vw,2.75rem)] font-bold leading-none tracking-[-.04em] text-[#93FF18] [text-shadow:0_0_30px_rgb(147_255_24/.28)] [@media(width>=32.5rem)]:whitespace-nowrap'>
                  <span className='whitespace-nowrap [@media(width<19.5rem)]:whitespace-normal' aria-hidden='true'>
                    <span className='cnt inline-block whitespace-nowrap text-end tabular-nums [@media(width<19.5rem)]:whitespace-normal' data-count={stat.count}>
                      {final}
                    </span>
                    +
                  </span>
                  <span className='sr-only'>{`${final}+`}</span>
                </p>
                <p className='mt-[.35rem] text-[.75rem] font-semibold uppercase tracking-[.1em] text-[#CBD5E1]'>{t(`scale.stats.${stat.key}.label`)}</p>
                <span className={STAT_BAR} aria-hidden='true'>
                  <i style={{ '--w': stat.bar } as CSSProperties} />
                </span>
                <p className='mt-[.65rem] text-[.75rem] leading-[1.45] text-[#9AA3B5]'>{t(`scale.stats.${stat.key}.note`)}</p>
              </li>
            );
          })}
        </ul>

        <ul className='mx-0 mb-0 mt-[clamp(1.25rem,.8rem+1.6vw,2rem)] grid list-none gap-[clamp(.75rem,.5rem+1vw,1.25rem)] p-0 [@media(width>=56rem)]:grid-cols-3' data-reveal-group>
          {QUOTES.map((quote) => (
            <li key={quote.key} className={QUOTE_CARD}>
              <blockquote className='text-[1rem] leading-[1.55] tracking-[-.005em] text-[#CBD5E1] [@media(width<22rem)]:[overflow-wrap:anywhere]'>{t(`scale.quotes.${quote.key}.text`)}</blockquote>
              <figure className='m-0 flex items-center gap-[.6rem] [&_b]:text-[.875rem] [&_b]:font-bold [&_b]:text-white [&_figcaption>span]:text-[.75rem] [&_figcaption>span]:text-[#9AA3B5] [&_figcaption]:grid [&_figcaption]:min-w-0 [@media(width<22rem)]:[&_figcaption>*]:[overflow-wrap:anywhere] [@media(width>=40rem)_and_(width<56rem)]:justify-start'>
                <span className='size-6 flex-none overflow-hidden rounded-full border border-[#93FF18]/45 bg-[#1C1E24] text-[.75rem] text-[#9AA3B5] shadow-[0_0_0_3px_rgb(147_255_24/.09)] [&_img]:block [&_img]:size-full [&_img]:object-cover [&_img]:object-[36%_50%] [@media(width<22rem)]:shadow-none'>
                  <Picture name={quote.photo} width={128} height={128} alt='' />
                </span>
                <figcaption>
                  <b>{quote.name}</b>
                  <span>{t(`scale.quotes.${quote.key}.role`)}</span>
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>

        <div className="mt-[clamp(1.5rem,1rem+1.6vw,2.25rem)] flex flex-wrap items-center gap-x-5 gap-y-[.9rem] border-t border-white/10 pt-[clamp(1.25rem,1rem+1vw,1.75rem)] [&_img[alt='DigiCert_Secured']]:h-6 [&_img]:block [&_img]:h-8 [&_img]:w-auto [&_img]:opacity-90 [@media(width<19.5rem)]:[&_img[alt='DigiCert_Secured']]:h-[1.15rem] [@media(width<19.5rem)]:[&_img]:h-6" data-reveal>
          <p className='text-[.6875rem] font-bold uppercase tracking-[.14em] text-[#9AA3B5]'>{t('scale.certified')}</p>
          <Picture name='cert-microsoft' width={96} height={99} alt='Microsoft Certified' />
          <Picture name='cert-digicert' width={128} height={52} alt='DigiCert Secured' />
          <p className='min-w-0 flex-[1_1_20rem] text-[.75rem] leading-normal text-[#9AA3B5]'>{t('scale.disclaimer')}</p>
        </div>
      </div>
    </section>
  );
}
